import { DateTime } from 'luxon';
import { DateRange, presetForDateRange } from '../../core/utils/date-range-presets';
import { ConversationMessage } from '../../service-gateways/converse/converse.interfaces';
import { CoVisrDisplayMessage, CoVisrWarning } from './co-visr.interfaces';

export interface BuildDisplayMessagesParams {
  messages: ConversationMessage[];
  heldAssistantMessage: ConversationMessage | null;
  streamingText: string;
  isStreaming: boolean;
  streamingMessageId: string | null;
  warnings?: CoVisrWarning[];
}

/** The sentence shown for a warning. One case per CoVisrWarning kind. */
export function formatWarning(warning: CoVisrWarning): string {
  switch (warning.kind) {
    case 'resultLimit':
      return (
        `To give an accurate picture, Covisr would need to go through ${warning.totalMatching.toLocaleString()} ` +
        `${warning.unit}, but can only analyze ${warning.maxPerRequest.toLocaleString()} at the moment.`
      );
    default: {
      // Compile-time exhaustiveness: a new CoVisrWarning kind without a case here fails to build,
      // instead of rendering a bare fallback nobody notices.
      const unhandled: never = warning.kind;
      return `Warning: ${String(unhandled)}`;
    }
  }
}

/** Presets that read naturally at the end of a question. `allTime` and `custom` are handled below. */
const PERIOD_PHRASES: Partial<Record<ReturnType<typeof presetForDateRange>, string>> = {
  today: 'today',
  yesterday: 'yesterday',
  thisWeek: 'this week',
  lastWeek: 'last week',
  thisMonth: 'this month',
  lastMonth: 'last month',
  last6Months: 'in the last 6 months',
  thisYear: 'this year',
  lastYear: 'last year',
};

/**
 * The selected period as words that end a question ("last week"), or '' for all time, where no
 * phrase reads better than "all time".
 */
export function formatPeriodPhrase(range: DateRange, now: Date = new Date()): string {
  const preset = presetForDateRange(range, now);
  if (preset === 'allTime') return '';
  if (preset !== 'custom') return PERIOD_PHRASES[preset] ?? '';

  const year = DateTime.fromJSDate(now).year;
  const format = (date: Date) => {
    const dt = DateTime.fromJSDate(date);
    return dt.toFormat(dt.year === year ? 'MMM d' : 'MMM d, yyyy');
  };
  return `between ${format(range.startDate)} and ${format(range.endDate)}`;
}

/**
 * Welcome-screen questions for the selected period. With a period they switch to the past tense,
 * which reads right for any period, including one still in progress; the rule one stays timeless.
 */
export function buildSuggestedQuestions(periodPhrase: string): string[] {
  if (!periodPhrase) {
    return [
      'Why are leads failing to qualify?',
      'Where in the conversation are we losing people?',
      'Show me calls where the agent let the customer off the hook.',
      'What compliance issues have come up, and why?',
      'Help me create a rule to track something new.',
    ];
  }
  return [
    `Why did leads fail to qualify ${periodPhrase}?`,
    `Where in the conversation did we lose people ${periodPhrase}?`,
    `Show me calls where the agent let the customer off the hook ${periodPhrase}.`,
    `What compliance issues came up ${periodPhrase}, and why?`,
    'Help me create a rule to track something new.',
  ];
}

/** Display text for a message that has only tool-call parts (e.g. "Execute CreateRule?"). */
export function getToolCallDisplayText(parts: ConversationMessage['parts']): string {
  const toolCallParts = parts.filter((p) => p.type === 'toolCall');
  const toolNames = toolCallParts
    .map((p) => p.toolName)
    .filter((n): n is string => !!n)
    .join(', ');
  return toolNames ? `Execute ${toolNames}?` : 'Confirm tool execution?';
}

/**
 * Builds the list of display messages for the co-visr chat UI from conversation
 * messages, optional held assistant message, and current streaming state.
 */
export function buildDisplayMessages(params: BuildDisplayMessagesParams): CoVisrDisplayMessage[] {
  const { messages, heldAssistantMessage, streamingText, isStreaming, streamingMessageId, warnings = [] } = params;

  const displayed: CoVisrDisplayMessage[] = [];
  // A warning sits just above the reply it belongs to, so the user reads it before the model's own
  // wording of the same thing.
  const pushWarningsFor = (messageId: string) => {
    for (const warning of warnings) {
      if (warning.messageId === messageId) {
        displayed.push({ id: warning.id, role: 'warning', text: formatWarning(warning) });
      }
    }
  };

  for (const msg of messages) {
    const textParts = msg.parts.filter((p) => p.type === 'text' && p.text);
    const toolCallParts = msg.parts.filter((p) => p.type === 'toolCall');
    if (msg.role === 'assistant') pushWarningsFor(msg.id);
    if (textParts.length > 0) {
      displayed.push({
        id: msg.id,
        role: msg.role,
        text: textParts.map((p) => p.text!).join(''),
      });
    } else if (msg.role === 'assistant' && toolCallParts.length > 0) {
      displayed.push({
        id: msg.id,
        role: 'assistant',
        text: getToolCallDisplayText(msg.parts),
      });
    }
  }

  if (heldAssistantMessage) {
    pushWarningsFor(heldAssistantMessage.id);
    const textParts = heldAssistantMessage.parts.filter((p) => p.type === 'text' && p.text);
    const toolCallParts = heldAssistantMessage.parts.filter((p) => p.type === 'toolCall');
    if (textParts.length > 0) {
      displayed.push({
        id: heldAssistantMessage.id,
        role: 'assistant',
        text: textParts.map((p) => p.text!).join(''),
      });
    } else if (toolCallParts.length > 0) {
      displayed.push({
        id: heldAssistantMessage.id,
        role: 'assistant',
        text: getToolCallDisplayText(heldAssistantMessage.parts),
      });
    }
  }

  if (isStreaming && streamingMessageId) pushWarningsFor(streamingMessageId);
  if (isStreaming && streamingMessageId && streamingText) {
    displayed.push({
      id: streamingMessageId,
      role: 'assistant',
      text: streamingText,
    });
  }

  return displayed;
}
